import { Box, IconButton, Tooltip } from '@mui/material';
import { mappedContractId, planDateWarnings } from '@timelog/pm-shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractHoursBar } from './ContractHoursBar';
import { ContractInsightHeading } from './ContractInsightHeading';
import { ContractKpiBoxes } from './ContractKpiBoxes';
import { ContractPicker } from './ContractPicker';
import { ContractPlanReading } from './ContractPlanReading';
import { contractOwnFactor, contractStaffing } from './contractStaffing';
import { usePlanEditor } from './editor';
import { buildHoursBar } from './hoursBarSegments';
import type { HoursBarSegmentKind } from './hoursBarSegments';
import { HOURS_LEGEND_CHIP, HOURS_SEGMENT_SX } from './hoursBarStyles';
import { displayPlanCode } from './planCode';
import { subtreeContractIds } from './subtreeContracts';

import { useBudgetModel } from '@/analytics';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import {
  IconedLabel,
  MICRO_LABEL_TRACKING,
  SectionHeading,
  SidePanelHeading,
  WrapGapRow,
} from '@/components/ui';
import { formatHours, formatShortDate } from '@/lib';

/** The rails' divider, 48px either side (2026-10-01): between the task and Contract insights. */
const DIVIDER_SX = {
  mt: 6,
  pt: 6,
  borderTop: 'var(--border-width-thin) solid var(--border-divider)',
} as const;

/** v8 legend vocabulary: Allocated / None / Over (logged is drawn, never listed). Allocated is the glossary's term. */
const HOURS_SEGMENT_LABEL_KEY = {
  logged: 'pmPlanningPanel:taskInsight.segment.logged',
  allocated: 'pmCommon:term.Allocated',
  unallocated: 'pmPlanningPanel:taskInsight.segment.unallocated',
  over: 'pmPlanningPanel:taskInsight.segment.over',
} as const satisfies Record<HoursBarSegmentKind, string>;

/** Side-panel mode while a row is active: everything about that one node. */
export const TaskInsightPanel = ({ nodeId }: { nodeId: string }) => {
  const {
    dispatch,
    flat,
    rollup,
    resolveRate,
    resolveCost,
    project,
    state,
    consumption,
    contractInputs,
  } = usePlanEditor();
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon']);
  const budgetModel = useBudgetModel();
  const metric = budgetModel.config.metric;
  const [notesOpen, setNotesOpen] = useState(false);

  const node = flat.index.nodeById.get(nodeId);
  const numbers = rollup.nodes[nodeId];
  if (!node || !numbers) return null;

  const hasChildren = node.children.length > 0;

  // Explicit mapping only (D8): a childless root maps itself like any leaf.
  const contractId = mappedContractId(node);
  /*
    The contracts Contract insights reads: a leaf's own, or — a parent maps
    nothing itself (ADR 0033) — every contract its subtasks map to (3 Nov,
    Mette), in the Financials order. Each is read as a whole: its Own Work
    budget against every task mapped to it, as a leaf's panel reads it.
  */
  const insightContractIds = hasChildren
    ? subtreeContractIds(
        node,
        contractInputs.map((contract) => contract.id),
      )
    : contractId === undefined
      ? []
      : [contractId];
  const insights = insightContractIds.flatMap((id) => {
    const contract = contractInputs.find((input) => input.id === id);
    const staffing = contractStaffing(
      id,
      state.document.plan,
      consumption,
      resolveRate,
      resolveCost,
    );
    if (!contract || !staffing) return [];
    // The uplift SET on the contract, for the forecast to be read against.
    return [{ contract, staffing, ownFactor: contractOwnFactor(contract, budgetModel.config) }];
  });

  /*
    CALC-22, off the rollup (BR-28). This used to be Σ every allocation in the
    subtree, computed here — which was both a second walk and the wrong number:
    it counted draft lines nobody is on, and it was being compared against a
    budget that WAS that same sum, so the bar could never show an unstaffed
    remainder or an overrun.
  */
  const allocatedHours = numbers.allocatedHours;
  const hoursBar = buildHoursBar({
    budgetHours: numbers.hours,
    allocatedHours,
    realizedHours: numbers.realized,
  });
  // BR-16, as warnings: the typed date is kept and the disagreement is named.
  const dateWarnings = planDateWarnings(node);
  return (
    <Box
      sx={{ pt: 2, animation: 'su var(--duration-fast) ease-out' }}
      data-automation-id="task-insight"
    >
      {/*
        The header (2026-09-28, the pilot's ask): the task's name and the notes
        toggle. Since 3 Nov (Mette) the name is the panel's title, styled as
        Web's Project Portfolio titles its preview panel (`SidePanelHeading`)
        with the plan code after it — the task is what the panel is about, so
        it comes first and largest, above sections that keep their small mono
        labels. The drawer's collapse row no longer says "Task summary".
      */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SidePanelHeading
            name={node.name}
            secondary={displayPlanCode(node.code)}
            nameAutomationId="task-insight-name"
          />
        </Box>
        {/* v8: a 26×26 circular OUTLINED button, filled while the notes are open. */}
        <IconButton
          size="tiny"
          title={notesOpen ? t('taskInsight.HideNotes') : t('taskInsight.ShowNotes')}
          aria-label={notesOpen ? t('taskInsight.HideNotes') : t('taskInsight.ShowNotes')}
          data-automation-id="task-insight-notes-toggle"
          onClick={() => setNotesOpen((open) => !open)}
          sx={{
            flex: 'none',
            width: 26,
            height: 26,
            border: 'var(--border-width-thin) solid var(--border-divider)',
            borderRadius: 'var(--border-radius-pill)',
            bgcolor: notesOpen ? 'var(--bg-sunken)' : 'transparent',
            '&:hover': { borderColor: 'var(--border-subtle)' },
          }}
        >
          <MaterialSymbol
            name="sticky_note_2"
            size={16}
            color={node.description ? 'var(--color-pink-80)' : 'var(--fg-3)'}
          />
        </IconButton>
      </Box>

      {notesOpen ? (
        <Box
          data-automation-id="task-insight-notes"
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            bgcolor: 'var(--bg-sunken)',
            borderRadius: 'var(--border-radius-medium)',
          }}
        >
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 500,
              letterSpacing: MICRO_LABEL_TRACKING,
              textTransform: 'uppercase',
              color: 'var(--fg-3)',
              mb: 0.5,
            }}
          >
            {t('taskInsight.Notes')}
          </Box>
          <Box
            component="textarea"
            rows={3}
            placeholder={t('taskInsight.NotesPlaceholder')}
            defaultValue={node.description ?? ''}
            key={node.id}
            data-automation-id="task-insight-notes-input"
            onBlur={(event) => {
              const text = (event.target as HTMLTextAreaElement).value.trim();
              const description = text === '' ? undefined : text;
              if (description !== node.description) {
                dispatch({ type: 'SET_NODE_FIELDS', nodeId: node.id, patch: { description } });
              }
            }}
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              border: 'var(--border-width-thin) solid var(--border-divider)',
              borderRadius: 'var(--border-radius-small)',
              bgcolor: 'var(--bg-panel)',
              outline: 'none',
              fontFamily: 'var(--font-family)',
              fontSize: 14,
              lineHeight: 1.45,
              color: 'var(--fg-1)',
              px: 1,
              py: '7px',
            }}
          />
        </Box>
      ) : null}

      {/*
        The task's details, right under its name, compressed into one wrapping
        line as Web's Project Portfolio panel lays out its customer and project
        manager (3 Nov, Mette): `IconedLabel`s in a `WrapGapRow`. The contract
        it bills through first — the one thing here to change, its badge and
        name as Contract insights shows it — then resources, dates and share of
        the plan; each says what it is on hover. A parent maps nothing and holds
        no allocations (ADR 0033, BR-2), so it shows its dates and share only.
        The date warnings stay, as the one thing here the row does not say.
      */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <WrapGapRow columnGap={3} rowGap={1} automationId="task-insight-details">
          {hasChildren ? null : (
            <>
              <Tooltip title={t('taskInsight.ContractPricingNote')} placement="top-start">
                <Box
                  component="span"
                  data-automation-id="task-insight-contract-label"
                  sx={{ display: 'inline-flex', minWidth: 0 }}
                >
                  <ContractPicker node={node} />
                </Box>
              </Tooltip>
              <IconedLabel
                icon="person"
                tooltip={t('taskInsight.ResourcesLabel')}
                automationId="task-insight-resources"
              >
                {t('taskInsight.Resources', { count: node.allocations.length })}
              </IconedLabel>
            </>
          )}
          <IconedLabel
            icon="calendar_today"
            tooltip={`${t('pmCommon:term.StartDate')} – ${t('pmCommon:term.TargetDate')}`}
            automationId="task-insight-dates"
          >
            <Box component="span" data-automation-id="task-insight-start">
              {formatShortDate(node.startDate)}
            </Box>
            {' – '}
            <Box component="span" data-automation-id="task-insight-target">
              {formatShortDate(node.targetDate)}
            </Box>
          </IconedLabel>
          <IconedLabel
            icon="pie_chart"
            tooltip={t('taskInsight.ShareOfPlan')}
            automationId="task-insight-share"
          >
            {t('taskInsight.PctOfPlanHours', { pct: Math.round(numbers.shareOfPlanPct) })}
          </IconedLabel>
        </WrapGapRow>
        {dateWarnings.length > 0 ? (
          <Box
            data-automation-id="task-insight-date-warnings"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              fontSize: 11,
              // A disagreement, not an error: BR-16 keeps the date and says so.
              color: 'var(--fg-warning-text)',
            }}
          >
            {dateWarnings.map((warning) => (
              <Box key={warning.code} component="span" data-warning={warning.code}>
                {/* Keyed by the shared code; the shared English `message` stays for the API. */}
                {t(`taskInsight.dateWarning.${warning.code}`)}
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>

      {/* Hours, the first section under the divider (3 Nov), as the portfolio's Insights. */}
      <Box sx={DIVIDER_SX} data-automation-id="task-insight-hours-section">
        <Box sx={{ mb: 2 }}>
          <SectionHeading icon="schedule">{t('pmCommon:term.Hours')}</SectionHeading>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Box
            component="span"
            data-automation-id="task-insight-hours"
            sx={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 24,
              fontWeight: 500,
              color: 'var(--fg-1)',
              lineHeight: 1,
            }}
          >
            {formatHours(numbers.hours)}
          </Box>
          <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {t('taskInsight.HoursBudgeted')}
          </Box>
        </Box>
        {/* Four-segment stack (v8, ADR 16): logged / allocated / unallocated /
            over — over is additive, never a clamp. Null model → a dash, no bar. */}
        {hoursBar ? (
          <>
            <Box
              data-automation-id="task-hours-bar"
              sx={{
                height: 6,
                borderRadius: 'var(--border-radius-pill)',
                overflow: 'hidden',
                display: 'flex',
                mb: '6px',
              }}
            >
              {hoursBar.segments.map((segment) => (
                <Box
                  key={segment.kind}
                  data-automation-id={`task-hours-segment-${segment.kind}`}
                  sx={{
                    height: '100%',
                    width: `${segment.pct}%`,
                    flex: 'none',
                    ...HOURS_SEGMENT_SX[segment.kind],
                    transition: 'width var(--duration-slow) var(--easing-standard)',
                  }}
                />
              ))}
            </Box>
            {/* v8: the legend carries the hour values — no separate footnote.
                Allocated renders even at 0 h; None and Over only when they hold
                hours; logged never (the rule lives in hoursBarSegments.ts). */}
            <Box
              data-automation-id="task-hours-footnote"
              sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}
            >
              {hoursBar.legend.map((entry) => (
                <Box
                  key={entry.kind}
                  component="span"
                  data-automation-id={`task-hours-legend-${entry.kind}`}
                  sx={{
                    fontSize: 10,
                    color: entry.kind === 'over' ? 'var(--color-coral-100)' : 'var(--fg-3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                    flex: 'none',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '2px',
                      bgcolor: HOURS_LEGEND_CHIP[entry.kind],
                      display: 'inline-block',
                      flex: 'none',
                    }}
                  />
                  {t('taskInsight.LegendEntry', {
                    label: t(HOURS_SEGMENT_LABEL_KEY[entry.kind]),
                    hours: formatHours(entry.hours),
                  })}
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ fontSize: 11, color: 'var(--fg-3)' }} data-automation-id="task-hours-footnote">
            {t('taskInsight.NoHoursYet')}
          </Box>
        )}
      </Box>

      {/*
        Contract insights, under the divider: since 3 Nov (Mette) the panel is
        two sections — the task (hours and its metadata, the contract among
        them) and the contract's insights. The contract picker that sat between
        them as "Map task to contract" is the Contract row above now.
      */}
      <Box sx={DIVIDER_SX} data-automation-id="task-insight-contract">
        <Box sx={{ mb: 2 }}>
          <SectionHeading icon="contract">{t('shared.ContractInsights')}</SectionHeading>
        </Box>
        {insights.length === 0 ? (
          <Box
            component="span"
            data-automation-id="task-insight-no-contract-insights"
            sx={{ color: 'var(--fg-3)', fontSize: 11 }}
          >
            {hasChildren
              ? t('taskInsight.SubtasksMapOwnContract')
              : t('taskInsight.NoContractInsights')}
          </Box>
        ) : (
          /*
            The contract's Own Work budget against the tasks mapped to it
            (2026-10-29, the designer's ask; reshaped 3 Nov, Mette): its hours
            first, as one bar styled like the task's Hours above with the
            allocated share beside the figure, then the money — the forecasted
            factor and the forecast — as the Financials aside's boxes. Planned
            and Allocated have no box here: there is nothing planned over time
            yet, and the bar says the allocated share. See `contractStaffing.ts`
            for the figures.

            A parent's blocks carry the project view's contract heading and its
            24px divider between them, so several contracts read apart; a
            leaf's one contract is named in the Contract row above.
          */
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              '& > [data-automation-id="task-insight-contract-block"] + [data-automation-id="task-insight-contract-block"]':
                {
                  mt: 3,
                  pt: 3,
                  borderTop: 'var(--border-width-thin) solid var(--border-divider)',
                },
            }}
          >
            {insights.map(({ contract, staffing, ownFactor }) => (
              <Box
                key={contract.id}
                data-automation-id="task-insight-contract-block"
                data-contract-id={contract.id}
              >
                {hasChildren ? (
                  <Box sx={{ mb: 2 }}>
                    <ContractInsightHeading
                      contract={contract}
                      metric={metric}
                      ownFactor={ownFactor}
                    />
                  </Box>
                ) : null}
                <ContractHoursBar staffing={staffing} />
                <ContractKpiBoxes
                  staffing={staffing}
                  contractType={contract.type}
                  currency={project.document.currency}
                  ownFactor={ownFactor}
                  metric={metric}
                  showPlanned={false}
                  showAllocated={false}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* What to do about it, as a section of its own (3 Nov, Mette's note). */}
      <ContractPlanReading
        metric={metric}
        groups={insights.map(({ contract, staffing, ownFactor }) => ({
          key: contract.id,
          label: contract.name,
          staffing,
          contractType: contract.type,
          ownFactor,
        }))}
      />
    </Box>
  );
};
